/* eslint-disable react/display-name */
import React, { CSSProperties, useState, forwardRef } from "react";
import { Ref } from "react";
import { FloatingLabel, Form } from "react-bootstrap";

import { iconEyeFill, iconEyeSlashFill } from "src/assets/images";
import { sanitizeDecimalInput, shouldAllowDecimalKeyDown, mergeDecimalPaste } from "src/utils/numericInput";

const TextField = forwardRef(
  (
    {
      type = "text",
      value = "",
      error = "",
      formText = "",
      eyeIcon,
      name,
      label,
      removeSpacing = "mb-3",
      onChange = () => { },
      onKeyPress = () => { },
      placeHolder = "",
      dataCy,
      as,
      id,
      style,
      disabled,
      className,
      maxLength,
      min,
      inputMode,
      decimalInput
    }: TextFieldProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const [showPassword, setShowPassword] = useState(false);


    const showPasswordHandle = () => {
      setShowPassword(!showPassword);
    };

    const floatingLabelStyle: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      width: "100%"
    };

    const stringValue = String(value ?? "");
    const displayValue = decimalInput ? sanitizeDecimalInput(stringValue) : value;

    const handleValueChange = (raw: string) => {
      onChange(decimalInput ? sanitizeDecimalInput(raw) : raw);
    };

    return (
      <Form.Group className={removeSpacing}>
        <FloatingLabel label={label} style={floatingLabelStyle}>
          <Form.Control
            ref={ref}
            type={type === "password" ? (showPassword ? "text" : "password") : type}
            value={displayValue}
            name={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleValueChange(e.currentTarget.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (!decimalInput) {
                return;
              }
              if (shouldAllowDecimalKeyDown(e, String(displayValue ?? ""))) {
                return;
              }
              e.preventDefault();
            }}
            onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
              if (!decimalInput) {
                return;
              }
              e.preventDefault();
              const input = e.currentTarget;
              const start = input.selectionStart ?? String(displayValue ?? "").length;
              const end = input.selectionEnd ?? start;
              handleValueChange(mergeDecimalPaste(String(displayValue ?? ""), e.clipboardData.getData("text"), start, end));
            }}
            placeholder={placeHolder}
            onKeyPress={(e: any) => onKeyPress(e)}
            noValidate
            autoComplete="off"
            data-cy={dataCy}
            id={id}
            as={as}
            style={style}
            disabled={disabled}
            className={className}
            maxLength={maxLength}
            min={min}
            inputMode={inputMode}
          />
          {eyeIcon ? (
            <button onClick={showPasswordHandle} className="password-eyeIcon" type="button" data-cy="eyeIcon">
              <img src={!showPassword ? iconEyeSlashFill : iconEyeFill} alt="icon" />
            </button>
          ) : (
            ""
          )}
        </FloatingLabel>
        {!error && formText && <Form.Text muted>{formText}</Form.Text>}
        <Form.Control.Feedback type="invalid" data-cy={dataCy + "Error"}>
          {error}
        </Form.Control.Feedback>
      </Form.Group>
    );
  }
);

export type TextFieldProps = {
  type?: inputType;
  name?: string;
  value?: string | number;
  error?: string;
  formText?: string;
  onChange?: (value: any) => void;
  onKeyPress?: (value: string) => void;
  placeHolder?: string;
  eyeIcon?: boolean;
  label?: React.ReactNode;
  dataCy?: string;
  id?: string;
  as?: React.ElementType; //string;
  style?: CSSProperties;
  removeSpacing?: string;
  disabled?: boolean;
  className?: string;
  maxLength?: number;
  min?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  decimalInput?: boolean;
};

export default TextField;
